import { useState, useEffect } from "react";
import { useLinkPreview } from "@/hooks/useLinkPreview";
import { useHandleLinkPress } from "@/utils/linkUtils";
import { decodeHtmlEntities } from "@tinyclub/shared/utils";
import { cn } from "./cn";

interface LinkPreviewProps {
  url: string;
  showLoadingIndicator?: boolean;
  className?: string;
}

const FALLBACK_ASPECT_RATIO = 1.91; // ~16:9 fallback

export function LinkPreview({
  url,
  showLoadingIndicator = true,
  className,
}: LinkPreviewProps) {
  const { preview, loading } = useLinkPreview(url);
  const handleLinkPress = useHandleLinkPress();
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null);
  const [imageError, setImageError] = useState(false);

  // Normalize URL - add https:// if no protocol specified
  const normalizedUrl = url.match(/^[a-zA-Z]+:\/\//) ? url : `https://${url}`;

  // Get image dimensions for card mode
  useEffect(() => {
    if (preview?.image && preview.isOgImage && !imageError) {
      const img = new window.Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        setImageAspectRatio(ratio);
      };
      img.onerror = () => {
        // For YouTube images that fail, try a known aspect ratio
        if (preview.image?.includes("ytimg.com")) {
          setImageAspectRatio(16 / 9);
        } else {
          setImageError(true);
          setImageAspectRatio(FALLBACK_ASPECT_RATIO);
        }
      };
      img.src = preview.image;
    } else {
      setImageAspectRatio(FALLBACK_ASPECT_RATIO);
    }
  }, [preview?.image, preview?.isOgImage, imageError]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleLinkPress(url);
  };

  // Loading state
  if (loading && showLoadingIndicator) {
    return (
      <div
        className={cn(
          "rounded-xl overflow-hidden bg-surface-1 p-4 flex items-center justify-center",
          className
        )}
      >
        <div className="w-5 h-5 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Safely parse URL for display
  let displayTitle: string;
  let displayDomain: string;
  try {
    const parsedUrl = new URL(normalizedUrl);
    displayTitle = decodeHtmlEntities(preview?.title || parsedUrl.hostname);
    displayDomain = parsedUrl.hostname.replace("www.", "");
  } catch {
    displayTitle = preview?.title || url;
    displayDomain = url;
  }

  const aspectRatio = imageAspectRatio || FALLBACK_ASPECT_RATIO;
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(displayDomain)}&sz=32`;

  return (
    <button
      onClick={handleClick}
      className={cn(
        "block w-full max-w-[320px] rounded-xl overflow-hidden bg-background text-left",
        "hover:opacity-90 transition-opacity",
        "relative",
        className
      )}
    >
      {/* OG Image */}
      {preview?.image && preview.isOgImage && !imageError && (
        <img
          src={preview.image}
          alt=""
          className="w-full object-cover"
          style={{ aspectRatio: aspectRatio.toString() }}
          onError={() => {
            // Don't mark YouTube images as errored
            if (preview.image && !preview.image.includes("ytimg.com")) {
              setImageError(true);
            }
          }}
        />
      )}

      {/* Text content */}
      <div className="p-3 bg-background">
        {/* Domain row with favicon */}
        <div className="flex items-center gap-1.5">
          <img
            src={faviconUrl}
            alt=""
            className="w-3 h-3"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <span className="text-body-3 text-text-tertiary truncate">
            {displayDomain}
          </span>
        </div>

        {/* Title */}
        <p className="text-body-2 text-text-primary mt-1 line-clamp-2">
          {displayTitle}
        </p>

        {/* Description */}
        {preview?.description && (
          <p className="text-body-3 text-text-tertiary mt-0.5 line-clamp-2">
            {decodeHtmlEntities(preview.description)}
          </p>
        )}
      </div>

      {/* Inner border overlay */}
      <div
        className="absolute inset-0 pointer-events-none rounded-xl"
        style={{ boxShadow: "inset 0 0 0 1px var(--color-border-tertiary)" }}
      />
    </button>
  );
}

export default LinkPreview;
